"use client";

import { useIsShopsite } from "@context/DomainContext";
import { XMarkIcon } from "@heroicons/react/24/solid";
import * as Toast from "@radix-ui/react-toast";
import { AnimatePresence, motion } from "framer-motion";
import { Text } from "@components/theming";
import { useNotificationStore } from "@store/notificationStore";
import { joinClassNames } from "@utils/helpers";
import { useWindowSize } from "@utils/hooks";
import { useTranslations } from "next-intl";

const colorVariants: { [key: string]: string } = {
  emerald: "text-emerald-400",
  red: "text-red-400",
  orange: "text-orange-400",
};

// Purely informational notifications with nothing for the user to act on
// (e.g. removed from cart, saved preferences). These auto-dismiss on their
// own after a few seconds.
export default function InfoNotification() {
  const notificationTranslations = useTranslations("Notification");
  const windowSize = useWindowSize();
  const show = useNotificationStore((state) => state.show);
  const setShow = useNotificationStore((state) => state.setShow);
  const content = useNotificationStore((state) => state.content);
  const isShopsite = useIsShopsite();
  const isMobile = !!windowSize.width && windowSize.width <= 640;

  const messageText =
    content?.content ===
    "Zone limit reached! Can't add more than 1 ticket per zone."
      ? notificationTranslations("zonelimitreached")
      : content?.content;

  if (isMobile) {
    return (
      <Toast.Provider swipeDirection="up">
        <Toast.Root
          open={show}
          onOpenChange={setShow}
          type="foreground"
          forceMount
          duration={5000}
          className={joinClassNames(
            "w-full outline-none",
            "data-[swipe=move]:transition-none data-[swipe=move]:translate-y-[var(--radix-toast-swipe-move-y)] data-[swipe=cancel]:translate-y-0 data-[swipe=end]:translate-y-[var(--radix-toast-swipe-end-y)] transition-transform"
          )}
        >
          <AnimatePresence>
            {show && (
              <motion.div
                key="info-toast-card"
                className={joinClassNames(
                  "overflow-hidden shadow-lg",
                  isShopsite
                    ? "bg-black rounded-b-2xl"
                    : "bg-[color:var(--gray-2)] rounded-[var(--radius-3)]"
                )}
                initial="hidden"
                animate="show"
                exit="hidden"
                variants={{
                  hidden: { height: 0, transition: { duration: 0.125 } },
                  show: { height: "auto" },
                }}
                transition={{ duration: 0.25 }}
              >
                <div className="flex items-center justify-between gap-3 p-4">
                  <div className="flex">
                    {content.icon ? (
                      <content.icon.icon
                        aria-hidden="true"
                        className={joinClassNames(
                          `shrink-0 h-6 w-6 ${
                            colorVariants[content.icon.color]
                          }`
                        )}
                      />
                    ) : null}
                    <div className="ml-3 pt-0.5">
                      <Text
                        as="p"
                        sharedClasses="text-sm font-medium"
                        shopsiteClasses="text-white"
                      >
                        {messageText}
                      </Text>
                    </div>
                  </div>
                  <Toast.Close
                    className={joinClassNames(
                      "inline-flex rounded-md h-min focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                      isShopsite
                        ? "text-gray-400 hover:text-gray-200 focus-visible:ring-indigo-500 focus-visible:ring-offset-black"
                        : "text-[color:var(--gray-a11)] hover:text-[color:var(--gray-a9)] focus-visible:ring-[color:var(--accent-a9)]"
                    )}
                  >
                    <span className="sr-only">
                      {notificationTranslations("close")}
                    </span>
                    <XMarkIcon className="w-5 h-5" aria-hidden="true" />
                  </Toast.Close>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Toast.Root>
        <Toast.Viewport className="fixed z-[49] outline-none flex flex-col top-[5.5rem] inset-x-0 w-full" />
      </Toast.Provider>
    );
  }

  return (
    <Toast.Provider swipeDirection="right">
      <Toast.Root
        open={show}
        onOpenChange={setShow}
        type="foreground"
        className={joinClassNames(
          "flex w-full p-4 shadow-lg pointer-events-auto backdrop-blur-popover ring-1 overflow-hidden",
          "data-[swipe=move]:transition-none transition-all data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=open]:slide-in-from-top-full data-[state=closed]:slide-out-to-right-full",
          isShopsite
            ? "bg-white ring-black ring-opacity-5 rounded-lg"
            : "bg-[color:var(--gray-2)] rounded-[var(--radius-3)]"
        )}
        duration={5000}
      >
        <Toast.Description className="flex flex-col w-full">
          <div className="flex items-center justify-between gap-3">
            <div className="flex">
              {content.icon ? (
                <content.icon.icon
                  aria-hidden="true"
                  className={joinClassNames(
                    `shrink-0 h-6 w-6 ${colorVariants[content.icon.color]}`
                  )}
                />
              ) : null}
              <div className="ml-3 pt-0.5">
                <Text
                  as="p"
                  sharedClasses="text-sm font-medium"
                  shopsiteClasses="text-black"
                >
                  {messageText}
                </Text>
              </div>
            </div>
            <Toast.Close
              className={joinClassNames(
                "inline-flex rounded-md h-min focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2",
                isShopsite
                  ? "text-gray-400 hover:text-gray-500 focus-visible:ring-indigo-500 focus-visible:ring-offset-white"
                  : "text-[color:var(--gray-a11)] hover:text-[color:var(--gray-a9)] focus-visible:ring-[color:var(--accent-a9)]"
              )}
            >
              <span className="sr-only">
                {notificationTranslations("close")}
              </span>
              <XMarkIcon className="w-5 h-5" aria-hidden="true" />
            </Toast.Close>
          </div>
        </Toast.Description>
      </Toast.Root>
      <Toast.Viewport className="fixed z-[1000] outline-none flex flex-col top-20 right-0 sm:top-[5.5rem] w-96 max-w-[100vw] p-6 gap-3" />
    </Toast.Provider>
  );
}
